import { Flex, message, Tag, Tooltip } from 'antd';
import { PencilIcon, StarIcon, TimerIcon } from '../../../assets/icons/icons';
import { COCRetryCall } from '../../../apis/shipment';
import { CustomButton, CustomParagraph } from '../../../components';
import { useNavigate } from 'react-router-dom';
import { useSpin } from '../../../components/SpinContext';
import { useToken } from '../../../common/helper';
import React, { useCallback, useState } from 'react';
import { useShipmentPermission } from '@/hooks/useShipmentPermission';
import { ErrorLike, Recipient } from '@/apis/types';
import { formatDateTime } from '@/common/utils/utils';
import type { NotificationData } from '../PerShipmentNotification/OneTimeNotificationModal';
import PerShipmentNotificationModal from '../PerShipmentNotification/PerShipmentNotificationModal';
import COCStatusIcons from './COCStatusIcons';
import Pill from '../AiTranscripts/ui/Pill';

interface StatusInfo {
  label?: string;
  description?: string;
  sentiment?: {
    type?: string;
  };
}

interface AIEvent {
  id: string;
  status_info?: StatusInfo;
  outcome?: string;
  ai_type: 'email' | 'call';
  title: string;
  timestamp: string;
  summary?: string;
  has_transcript: boolean;
  dismissed?: boolean;
  resolved?: boolean;
}

interface COCAlertData {
  id: string;
  triggered_by: string;
  message?: string;
  scheduled_at?: string;
  is_sent?: boolean;
  recipients?: Recipient[];
  ai_events?: AIEvent[];
  eventId?: string;
  is_resolved?: boolean;
  is_dismissed?: boolean;
  is_send_now?: boolean;
}

export interface COCFormattedTime {
  full: string;
  relative: string;
}

interface RecipientsDisplayProps {
  recipients?: Recipient[];
}

interface OneTimeCOCAlertProps {
  isControlTower?: boolean;
  data: COCAlertData;
  getFormattedTime: (timestamp: string) => COCFormattedTime;
  setShowFull: React.Dispatch<React.SetStateAction<boolean>>;
  showFull: boolean;
  shipmentId: string;
  toggleDrawer?: (
    action: string,
    type: string,
    shipmentId: string,
    data: COCAlertData
  ) => void;
  parentRef: React.RefObject<HTMLParagraphElement | HTMLDivElement | null>;
  repliesRef: React.RefObject<HTMLParagraphElement | HTMLDivElement | null>;
  canViewTranscripts?: boolean;
  /** AI communications edit for this shipment (retry requires it; transcript view alone does not). */
  canEditAiCommunications?: boolean;
  fetchSingleShipmentData?: (showSpinner: boolean) => void;
}

/**
 * Component to display recipients with tooltip for multiple recipients
 *
 * @param recipients - Array of recipient objects
 */
const RecipientsDisplay: React.FC<RecipientsDisplayProps> = ({
  recipients = [],
}) => {
  if (recipients?.length === 0) return null;

  if (recipients?.length === 1) {
    return (
      <span>
        {recipients[0]?.name}
        {recipients[0]?.role && ` (${recipients[0]?.role})`}
      </span>
    );
  }
  const [first, ...rest] = recipients;
  return (
    <span>
      {first?.name} ({first?.role})
      <Tooltip
        title={rest.map((r) => (
          <div key={r?.id}>
            {r?.name} {r?.role && ` (${r?.role})`}
          </div>
        ))}
      >
        <span className='cursor-pointer'>+{rest?.length}</span>
      </Tooltip>
    </span>
  );
};

/**
 * OneTimeCOCAlert component for displaying Chain of Custody alerts with AI events
 *
 * @param data - COC alert data including AI events and recipients
 * @param getFormattedTime - Function to format timestamps
 * @param setShowFull - State setter for show full time format
 * @param showFull - Boolean for time display format
 * @param shipmentId - ID of the shipment
 * @param toggleDrawer - Function to toggle transcript drawer
 * @param parentRef - Ref for parent paragraph element
 * @param repliesRef - Ref for replies paragraph element
 */
const OneTimeCOCAlert: React.FC<OneTimeCOCAlertProps> = ({
  isControlTower = true,
  data,
  getFormattedTime,
  setShowFull,
  showFull,
  shipmentId,
  toggleDrawer,
  parentRef,
  repliesRef,
  fetchSingleShipmentData,
  canViewTranscripts = true,
  canEditAiCommunications = false,
}) => {
  const { showSpin, hideSpin } = useSpin();
  const [openNotificationModal, setOpenNotificationModal] = useState(false);
  const canCreateShipmentForSupport = useShipmentPermission();
  const canRetryAiCall = canCreateShipmentForSupport && canEditAiCommunications;
  const navigate = useNavigate();
  const { token, removeToken } = useToken();
  const unauthorize = useCallback(
    (error: ErrorLike) => {
      if (error && error?.status === 401) {
        removeToken();
        Promise.resolve(navigate('/login')).catch(console.error);
      }
    },
    [removeToken, navigate]
  );
  const handleRetryCall = (): void => {
    showSpin();
    const model = {
      reason: 'Call failed due to network issues',
    };
    COCRetryCall(token ?? '', model, data?.id)
      .then((res) => {
        const responseData = res?.data;
        if (responseData?.data) {
          if (responseData.data.is_success) {
            fetchSingleShipmentData?.(false);
            message.success(responseData.data.message);
          } else {
            message.error(responseData.data.message);
          }
        }
      })
      .catch((error: ErrorLike) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  };
  // Callers share one ref type between <p> and <div> rows; it is only measured.
  const parentParagraphRef = parentRef as React.RefObject<HTMLParagraphElement>;
  const repliesParagraphRef =
    repliesRef as React.RefObject<HTMLParagraphElement>;
  return (
    <>
      <div>
        <p
          ref={parentParagraphRef}
          className='zl-para-12 coc-comment-editor mt-1'
        >
          Triggered by {data?.triggered_by} →{' '}
          <RecipientsDisplay recipients={data?.recipients || []} />
        </p>

        <div className='mt-2 one-time-message-wrapper flex-center y btw color-black coc-comment-editor zl-para-12'>
          <p ref={repliesParagraphRef}>Message: {data?.message}</p>
          {!data?.is_send_now && (
            <Flex gap={8} align='center'>
              <Tooltip
                placement='top'
                title={
                  <p className='zl-para-10 color-white'>
                    Scheduled: {formatDateTime(data?.scheduled_at)}
                  </p>
                }
                arrow={true}
              >
                <span className='h-4'>
                  <TimerIcon />
                </span>
              </Tooltip>
              {canCreateShipmentForSupport && (
                <Tooltip
                  placement='top'
                  title={<p className='zl-para-10 color-white'>Edit message</p>}
                  arrow={true}
                >
                  <span className='h-4'>
                    <div
                      onClick={() => {
                        setOpenNotificationModal(true);
                      }}
                    >
                      <PencilIcon />
                    </div>
                  </span>
                </Tooltip>
              )}
            </Flex>
          )}
        </div>
        {data?.ai_events?.map((item: AIEvent) => (
          <Flex gap={12} key={item.id} className='mt-5'>
            <div className='one-time-action-type-icon'>
              {item?.status_info?.description === 'Needs attention' && (
                <div className='need-attention-dot'></div>
              )}
              {/* status icons for AI events */}
              <COCStatusIcons item={item} />
            </div>
            <div className='w-full flex-center x flex-col'>
              <div>
                <Flex justify='space-between'>
                  <p className='zl-para-12'>{item?.title}</p>
                  <span
                    onClick={() => setShowFull((prev: boolean) => !prev)}
                    className='cursor-pointer'
                  >
                    <CustomParagraph
                      text={
                        showFull
                          ? getFormattedTime(item?.timestamp)?.full
                          : getFormattedTime(item?.timestamp)?.relative
                      }
                      fontSize='12px'
                      fontWeight={400}
                      lineHeight='16px'
                      color='var(--text-light)'
                      marginBottom='0'
                      style={{ whiteSpace: 'nowrap' }}
                    />
                  </span>
                </Flex>
                <Flex>
                  {item?.status_info?.label === 'In Progress' && (
                    <Tag className='mt-2 mb-2 one-time-status-tag processing'>
                      Processing
                    </Tag>
                  )}
                  {item?.status_info?.description === 'Needs attention' && (
                    <Tag className=' mt-2 mb-2 one-time-status-tag need-attention'>
                      Needs attention
                    </Tag>
                  )}
                  {item?.status_info?.description === 'Resolved via AI' && (
                    <Pill style={{ width: 'fit-content' }}>
                      {item?.status_info?.description}
                    </Pill>
                  )}
                  {item?.status_info?.description === 'Dismissed' && (
                    <Pill style={{ width: 'fit-content' }}>
                      {item?.status_info?.description}
                    </Pill>
                  )}

                  {item?.status_info?.sentiment?.type === 'negative' && (
                    <Flex align='center' gap={4}>
                      <div className='on-time-negtive-dot'></div>
                      <p className='zl-para-10 font-600'>Negative sentiment</p>
                    </Flex>
                  )}
                </Flex>

                {item?.summary && (
                  <Flex gap={4} align='center'>
                    <p className='color-black coc-comment-editor zl-para-12 mt-2 mb-1 max-w-92'>
                      {item?.summary}
                    </p>
                    <span className='flex-center AI-star-fill-color'>
                      <StarIcon width={24} height={24} />
                    </span>
                  </Flex>
                )}

                {isControlTower && (
                  <Flex gap={7}>
                    {canViewTranscripts && item?.has_transcript && (
                      <CustomButton
                        text={
                          item?.ai_type === 'call'
                            ? 'View call transcript'
                            : 'View email transcript'
                        }
                        bgColor='var(--light-gray)'
                        marginTop='6px'
                        color='black'
                        fontSize='12px'
                        fontWeight='600'
                        borderRadius='28px'
                        padding='6px 8px'
                        boxShadow='none'
                        height='28px'
                        onClick={() => {
                          toggleDrawer?.(
                            'open',
                            item?.ai_type === 'call'
                              ? 'callTranscripts'
                              : 'emailTranscripts',
                            shipmentId,
                            { ...data, eventId: item.id }
                          );
                        }}
                      />
                    )}
                    {canRetryAiCall &&
                    item?.ai_type === 'call' &&
                    item?.outcome?.toLowerCase() === 'not answered' ? (
                      <CustomButton
                        text='Retry now'
                        bgColor='var(--light-gray)'
                        marginTop='6px'
                        color='black'
                        fontSize='12px'
                        fontWeight='600'
                        borderRadius='28px'
                        width='75px'
                        boxShadow='none'
                        height='28px'
                        onClick={() => {
                          handleRetryCall();
                        }}
                      />
                    ) : null}
                  </Flex>
                )}
              </div>
            </div>
          </Flex>
        ))}
      </div>
      {openNotificationModal && (
        <PerShipmentNotificationModal
          // eslint-disable-next-line no-restricted-syntax -- modal's `selected` prop is non-nullable but null means "load by notificationId"
          selected={null as unknown as NotificationData}
          notificationId={data?.id}
          fetchNotifications={fetchSingleShipmentData}
          shipmentId={shipmentId}
          notificationModalType={2}
          openNotificationModal={openNotificationModal}
          setOpenNotificationModal={setOpenNotificationModal}
        />
      )}
    </>
  );
};

export default OneTimeCOCAlert;
